import fetch from 'isomorphic-fetch';
import { print } from 'graphql';
import { OffersByIdsDocument } from '@graphql/shared/offers-by-ids.generated';
import { ShopsByIdsDocument } from '@graphql/shared/shops-by-ids.generated';
import { BASE_SITE_URL } from './constants';
import type { Locale } from './locale';
import { parseFacetResult, parseSearchResult } from './search-contract';

// Returns a promise where the data is in `res.data.shopsByIds`
export const apiQueryShopsByIds = (
  ids: string[] | null,
  locale: Locale,
  httpBase = '',
): Promise<unknown> | null => {
  if (!ids?.length) return null;

  const fetchOptions: RequestInit = {
    method: 'post',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      query: print(ShopsByIdsDocument),
      variables: {
        ids,
      },
    }),
  };

  const fetchUrl = `${httpBase}/api/${locale}/graphql/apiQueryShopsByIds`;

  return fetch(fetchUrl, fetchOptions).then(async (res): Promise<unknown> => {
    if (res.status === 200) {
      const value: unknown = await res.json();
      return value;
    }
    throw new Error(`Shops API failed with status ${res.status}`);
  });
};

// Returns a promise where the data is in `res.data.offersByIds`
export const apiQueryOffersByIds = (
  ids: string[] | null,
  locale: Locale,
  httpBase = '',
): Promise<unknown> | null => {
  // cannot return null and call 'then' - need to fix
  if (!ids) return null;

  const fetchOptions: RequestInit = {
    method: 'post',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      query: print(OffersByIdsDocument),
      variables: {
        ids,
      },
    }),
  };

  const fetchUrl = `${httpBase}/api/${locale}/graphql/apiQueryOffersByIds`;

  return fetch(fetchUrl, fetchOptions).then(async (res): Promise<unknown> => {
    if (res.status === 200) {
      const value: unknown = await res.json();
      return value;
    }

    throw new Error(`Offers API failed with status ${res.status}`);
  });
};

const baseSiteUrl = () => BASE_SITE_URL ?? 'https://earnmore.bluerewards.ca';

export const apiSearchAlgolia = async (
  searchQuery: string,
  searchOptions: Record<string, unknown>,
  indexType: string,
  locale: Locale,
) => {
  const url = baseSiteUrl();

  const response = await fetch(`${url}/${locale}/api/searchAlgolia`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      indexType,
      searchOptions,
      searchQuery,
    }),
  });
  if (!response.ok) throw new Error(`Search API failed with status ${response.status}`);
  const value: unknown = await response.json();
  return parseSearchResult(value);
};

export const apiSearchForFacetsAlgolia = async (
  categoryFacetSearchOptions: Record<string, unknown>,
  indexType: string,
  locale: Locale,
) => {
  const url = baseSiteUrl();

  const response = await fetch(`${url}/${locale}/api/searchForFacetAlgolia`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      indexType,
      categoryFacetSearchOptions,
    }),
  });
  if (!response.ok) throw new Error(`Search facet API failed with status ${response.status}`);
  const value: unknown = await response.json();
  return parseFacetResult(value);
};
